import { Component, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Button, Card } from '@crave/ui';
import { PAYMENT_LOAD_ERROR, syncPaymentUntilSettled, type PaymentProps } from '../lib/payment.ts';
import type { StripeElementsProps } from './StripeElements.tsx';

/**
 * The card form, kept out of every page's import graph: Stripe's library puts its own script on the
 * page as soon as it is imported, so the menu, item and cart pages would otherwise load Stripe too.
 * It is fetched on its own instead, and `preloadPaymentForm` starts that fetch as soon as a screen
 * knows a card may be entered, so the form is already in hand by the time the customer gets to it.
 */
type ElementsModule = { StripeElements: ComponentType<StripeElementsProps> };

let pending: Promise<ElementsModule> | null = null;

function loadForm(): Promise<ElementsModule> {
  pending ??= import('./StripeElements.tsx').catch((e: unknown) => {
    // A dropped request must not be remembered, or every later try repeats the same failure.
    pending = null;
    throw e;
  });
  return pending;
}

/**
 * Start fetching the card form before it is on screen. Safe to call as often as you like: the fetch
 * happens once. A failure here is quiet; the form tries again when it is actually shown.
 */
export function preloadPaymentForm(): void {
  void loadForm().catch(() => {
    /* Nothing to show yet, so nothing to say yet. */
  });
}

/** Wait a beat before trying a dropped fetch again. */
const RETRY_MS = 1200;

/**
 * Stripe's own code throws for things we cannot check first — an intent that has already been used,
 * a secret that has gone stale. Without this the whole screen goes blank, which is the worst answer
 * a customer holding a card can get. It degrades to a message and a way to try again instead.
 */
class FormBoundary extends Component<
  { onReset: () => void; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Card>
        <p style={{ color: 'var(--crave-red)', margin: 0 }}>{PAYMENT_LOAD_ERROR}</p>
        <div style={{ marginTop: 12 }}>
          <Button
            variant="secondary"
            onClick={() => {
              this.setState({ failed: false });
              this.props.onReset();
            }}
          >
            Try again
          </Button>
        </div>
      </Card>
    );
  }
}

export function StripePayment(props: StripeElementsProps) {
  const [Form, setForm] = useState<ComponentType<StripeElementsProps> | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    void (async () => {
      // One quiet retry first: a phone that lost the request for a moment still gets the form.
      for (let tries = 0; tries < 2; tries++) {
        try {
          const m = await loadForm();
          if (live) setForm(() => m.StripeElements);
          return;
        } catch {
          if (!live) return;
          if (tries === 0) await new Promise((r) => setTimeout(r, RETRY_MS));
        }
      }
      if (live) setFailed(true);
    })();
    return () => {
      live = false;
    };
  }, [attempt]);
  if (failed)
    return (
      <Card>
        <p style={{ color: 'var(--crave-red)', margin: 0 }}>{PAYMENT_LOAD_ERROR}</p>
        <div style={{ marginTop: 12 }}>
          <Button
            variant="secondary"
            onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </Button>
        </div>
      </Card>
    );
  if (!Form) return <p>Loading secure payment…</p>;
  return (
    <FormBoundary key={attempt} onReset={() => setAttempt((n) => n + 1)}>
      <Form {...props} />
    </FormBoundary>
  );
}

/** Development-only stand-in when the API runs with the fake provider. */
export function FakePayment({ orderId, onSettled }: Pick<PaymentProps, 'orderId' | 'onSettled'>) {
  const [busy, setBusy] = useState(false);
  return (
    <Card>
      <p>Test mode: no real payment provider is connected.</p>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          const synced = await syncPaymentUntilSettled(orderId);
          setBusy(false);
          onSettled(synced);
        }}
      >
        {busy ? 'Confirming…' : 'Continue'}
      </Button>
    </Card>
  );
}
